import { TranslateResult } from 'vue-i18n';
import { i18n } from '../services/translation.service';

import { BookingDropdownOption } from '../models/entities/booking.model';

import { Maybe, SchedulePreference, TimeSlotInterval } from '../graphql/schema';

import DateService from '../services/date.service';

export default class BookingService {
  static buildBookingTimeLabel(
    localeCode: string,
    schedule: SchedulePreference,
    dealerName: string
  ): TranslateResult {
    const date = {
      start: schedule.pickUpDateTime,
      label: `${BookingService.formatTimeStamp(
        schedule.pickUpDateTime,
        localeCode
      )} - ${BookingService.formatTimeStamp(
        schedule.returnDateTime,
        localeCode
      )}`,
    };
    const period = DateService.buildPeriodLabel(date, true) as string;
    return BookingService.buildTimeLabel(localeCode, date, period, dealerName);
  }

  static buildRequestTimeLabel(
    localeCode: string,
    schedule: any,
    dealerName: string
  ): TranslateResult {
    const date = {
      start: schedule.start,
      periodId: schedule.dayPeriod,
    };
    const period = DateService.buildPeriodLabel(date, false) as string;
    return BookingService.buildTimeLabel(localeCode, date, period, dealerName);
  }

  static buildTimeLabel(
    localeCode: string,
    date: any,
    period: string,
    dealerName: string
  ): TranslateResult {
    const settings = {
      'schedule-date-period': DateService.getSelectedPeriodSummary(
        new Date(date.start),
        period,
        localeCode
      ),
      'dealer-computedName': dealerName,
    };
    return i18n.t('summary.timeAndPlaceDealer.template', settings);
  }

  static convertToSchedulePreference(
    localeCode: string,
    pickUpDate: string,
    returnDate: string
  ): SchedulePreference {
    return {
      pickUpDateStr: DateService.getTime(pickUpDate, localeCode),
      pickUpDateTime: pickUpDate,
      returnDateStr: DateService.getTime(returnDate, localeCode),
      returnDateTime: returnDate,
    };
  }

  static buildSlot(
    slot: TimeSlotInterval,
    locale: string
  ): BookingDropdownOption {
    const label = `${BookingService.formatTimeStamp(
      slot.start,
      locale
    )} - ${BookingService.formatTimeStamp(slot.end, locale)}`;
    return {
      value: { ...slot, label },
      text: label,
    };
  }

  static formatTimeStamp(timeStamp: string, locale: string): string {
    return DateService.getTime(timeStamp, locale);
  }

  static getBookingSlots(
    bookingSlots: Maybe<TimeSlotInterval[]>,
    locale = 'default',
    isBooking: boolean
  ): BookingDropdownOption[] {
    let slots: BookingDropdownOption[] = [];
    if (isBooking && bookingSlots?.length) {
      slots = BookingService.mapBookingSlots(bookingSlots, locale);
    }
    return slots;
  }

  // eslint-disable-next-line max-len
  static mapBookingSlots(
    bookingTimeSlots: TimeSlotInterval[],
    locale: string
  ): BookingDropdownOption[] {
    return bookingTimeSlots.map((slot) =>
      BookingService.buildSlot(slot, locale)
    );
  }
}
